"use client";
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import Reveal from '../../enterprise/components/Reveal';
import Link from 'next/link';

const RoadmapItem = ({ item, index, isActiveLogo, onRefUpdate }: any) => {
    const itemRef = useRef<HTMLDivElement>(null);
    const [isActive, setIsActive] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const quarterRefs = useRef<(HTMLDivElement | null)[]>([]);

    useEffect(() => {
        onRefUpdate(index, itemRef.current);
    }, [index, onRefUpdate]);

    useEffect(() => {
        const handleScroll = () => {
            if (!itemRef.current) return;

            const rect = itemRef.current.getBoundingClientRect();
            const windowHeight = window.innerHeight;
            const halfScreen = windowHeight / 2;

            const elementTop = rect.top;
            const elementHeight = rect.height;

            const isInTopHalf = elementTop < halfScreen && (elementTop + elementHeight) > 0;

            setIsActive(isInTopHalf);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();

        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const isQuarterActive = (qIndex: number) => {
        if (!quarterRefs.current[qIndex]) return false;

        const rect = quarterRefs.current[qIndex]?.getBoundingClientRect();
        if (!rect) return false;

        const windowHeight = window.innerHeight;
        const halfScreen = windowHeight / 2;
        const elementTop = rect.top;
        const elementHeight = rect.height;

        return elementTop < halfScreen && (elementTop + elementHeight) > 0;
    };

    return (
        <div ref={itemRef} className="flex items-start justify-between w-[100%] min-h-[100px]">
            <div className="w-[45%] flex justify-center max-sm:hidden">
            </div>

            <div className={`w-[45%] max-sm:w-[100%] max-sm:px-8 max-sm:py-4 transition-opacity duration-500 ${isActive ? 'opacity-100' : 'opacity-30'
                }`}>
                <h3 className="text-[32px]/[40px] font-semibold text-[#06f] mb-[100px]">{item.title}</h3>

                <div className="space-y-[100px]">
                    {item.quarters.map((quarter: any, qIndex: number) => (
                        <div key={qIndex} className="relative flex items-start">
                            <div
                                ref={(el) => { quarterRefs.current[qIndex] = el; }}
                                className="w-full"
                            >
                                <div className="flex items-center mb-6 relative">
                                    {/* Icon được đặt ở đây, căn giữa timeline */}
                                    <div className="absolute -left-[calc(13%)] max-sm:left-[-28px] top-0 bg-white z-10">
                                        <Image
                                            src="/images/icons/logo-network.svg"
                                            alt="dot"
                                            width={24}
                                            height={24}
                                            className={`${isQuarterActive(qIndex) ? 'opacity-100' : 'opacity-100'
                                                }`}
                                        />
                                    </div>
                                    <h4 className="font-semibold text-[#202020] text-[24px]/[32px]">{quarter.period}</h4>
                                </div>
                                <div className={`pl-6 max-sm:pl-4 space-y-4 overflow-hidden transition-all duration-300 ${isExpanded ? 'max-h-[1000px]' : 'max-h-[200px]'
                                    }`}>
                                    {quarter.tasks.map((task: string, tIndex: number) => (
                                        <p key={tIndex} className={` text-[#646464] leading-relaxed ${!isExpanded && tIndex >= 4 ? 'opacity-30' : ''
                                            }`}>
                                            {task}
                                        </p>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default function RoadMapAboutSection() {

    const sectionRef = useRef<HTMLDivElement>(null);
    const [activeItem, setActiveItem] = useState<number | null>(0);
    const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
    const t = useTranslations('enterprise');
    const roadmapData = [
        {
            title: t('phase1_title'),
            quarters: [
                {
                    period: 'Q1-Q2 • 2022',
                    tasks: [
                        t('market_research_and_technology_assessment'),
                        t('initial_architecture_design'),
                        t('detailed_roadmap_development'),
                        t('whitepaper_drafting'),
                    ]
                },
                {
                    period: 'Q3-Q4 • 2022',
                    tasks: [
                        t('internal_development_and_testing_of_hyra_network_blockchain'),
                        t('core_hyra_ai_platform_development_and_refinement'),
                        t('blockchain_infrastructure_development'),
                        t('smart_contract_finalization'),
                    ]
                }
            ]
        }

    ]

    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ["start end", "end start"]
    });

    const rawHeight = useTransform(scrollYProgress, () => {
        if (!sectionRef.current) return '0%';

        const rect = sectionRef.current.getBoundingClientRect();
        const winH = window.innerHeight;
        const half = winH / 2;

        const topVisible = Math.max(0, half - rect.top);
        const maxH = Math.min(rect.height, topVisible);
        const percent = (maxH / rect.height) * 100;

        return `${Math.max(0, percent)}%`;
    });
    const numericHeight = useTransform(rawHeight, v => +v.replace('%', ''));

    const smoothNumeric = useSpring(numericHeight, {
        stiffness: 200,
        damping: 24,
        mass: 0.8
    });

    const smoothHeight = useTransform(smoothNumeric, v => `${v}%`);
    const barPx = useTransform(smoothNumeric, v => {
        if (!sectionRef.current) return 0;

        const sectionH = sectionRef.current.getBoundingClientRect().height * 0.95;
        const y = (v / 100) * sectionH;

        return Math.min(y, sectionH);
    });
    const logoY = useTransform(barPx, y =>
        y <= 150 ? 0
            : y - 185
    );

    useEffect(() => {
        const handleScroll = () => {
            const windowHeight = window.innerHeight;
            const centerY = windowHeight / 2;
            let closestIndex = null;
            let closestDistance = Infinity;

            itemRefs.current.forEach((ref, index) => {
                if (!ref) return;

                const rect = ref.getBoundingClientRect();
                const itemCenterY = rect.top + rect.height / 2;
                const distance = Math.abs(itemCenterY - centerY);

                if (rect.top < windowHeight && rect.bottom > 0 && distance < closestDistance) {
                    closestDistance = distance;
                    closestIndex = index;
                }
            });

            setActiveItem(closestDistance < 200 ? closestIndex : 0);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();

        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const updateItemRef = (index: number, ref: HTMLDivElement | null) => {
        itemRefs.current[index] = ref;
    };

    return (
        <section className="py-20" id="roadmap">
            <div className="flex flex-col justify-center items-center text-center">
                <p className="font-open-sans text-base text-[#06f] font-normal uppercase">{t('6-key-phases')}</p>
                <Reveal
                    delay={0.1}
                    duration={0.8}
                    direction="left"
                    withBlur={true}
                    blurAmount={8}
                >
                    <h2 className="text-center text-[48px]/[56px] max-sm:text-2xl tracking-[-1px] mt-12">{t('roadmap')}</h2>
                </Reveal>
                <Reveal
                    delay={0.5}
                    duration={0.8}
                    direction="left"
                    withBlur={true}
                    blurAmount={8}
                >
                    <p className="mt-6 text-base/[24px] max-sm:text-sm font-light text-[#646464] max-w-[661px]"> {t('six-phase-roadmap-from-2025-to-2030-genesis-decentralization-maturation-expansion-ipo-preparation-and-global-dao-governance')}</p>
                </Reveal>
            </div>
            <div className="relative overflow-hidden mt-[56px] max-w-[1440px] mx-auto">
                <div ref={sectionRef} className="absolute h-[95%] translate-y-4 left-1/2 max-sm:left-4 -translate-x-1/2">
                    <div className="w-1 h-full bg-[#06f] opacity-10"></div>
                    <motion.div
                        className="absolute top-0 left-0 w-1 bg-[#06f] opacity-100"
                        style={{ height: smoothHeight }}
                    >
                    </motion.div>

                    <motion.div
                        className="absolute -left-[400px] max-lg:-left-[350px] max-md:-left-[300px] max-sm:hidden top-0"
                        style={{ translateY: logoY }}
                    >
                        <Image src="/images/icons/new-logo.svg" alt="Hyra Logo" width={250} height={200} className="max-h-[316px]" />
                    </motion.div>
                </div>
                {roadmapData.map((item, index) => (
                    <RoadmapItem
                        key={index}
                        item={item}
                        index={index}
                        isActiveLogo={activeItem === index}
                        onRefUpdate={updateItemRef}
                    />
                ))}
            </div>

            <div className="flex justify-center pt-12">
                <Link href={"/roadmap"}>
                    <button className="bg-[#06f] text-white px-[33px] py-[14px] rounded-lg font-semibold hover:bg-blue-700 transition-colors">
                        {t("see_all")}
                    </button>
                </Link>
            </div>
        </section>
    )
}